import { Button } from '@forecastify/ui';
import { useT } from '../i18n';
import { useSession } from '../session';
import { AuthFrame } from './auth/auth-frame';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ The tenant is suspended. S3, `decisions.md` S-9.                          │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THIS SCREEN IS WHY THE REFUSAL IS A 403 WITH ITS OWN CODE RATHER THAN A 404.** Every
 * workspace-scoped request from a suspended tenant's member is refused by the API guard. Without a
 * distinguishable code the app would know only that everything fails — which is indistinguishable
 * from the API being down, and would put *"a szerver nem válaszol"* in front of somebody whose
 * account is simply paused. Rule 5's *"existence is itself information"* does not apply: the caller
 * signed in, belongs to this workspace, and is active. It is **their own** tenant.
 *
 * ⚠️ **The suspension REASON is not on this page, and that is deliberate.** `tenants.suspension_reason`
 * is the operator's own note (0033) — free text, written for a colleague six months later, in
 * whatever language they typed it in. Showing it here would leak an internal note to the customer
 * and would bypass the catalogue, which is where every user-facing string lives.
 *
 * ⚠️ **`AuthFrame`, for the reason the first-run setup screen gives its own frame.** The frame draws *the page with no
 * workspace behind it*, and a suspended tenant has one it cannot enter. `AppShell` would need a
 * membership it must not use — and a sidebar of links that all 403 is worse than no sidebar.
 *
 * ⚠️ **Sign-out is the only control, and it has to work.** `POST /auth/sign-out` is account-level
 * (`@Public()`, `resolveAccount`) and therefore unaffected by the suspension — which is the half of
 * S-9 that keeps this from being a dead end. A screen with no way off it, for somebody who may
 * simply be signed in as the wrong person, is the dead end `/welcome` was built to remove.
 */
export function SuspendedScreen() {
  const t = useT();
  const session = useSession();

  return (
    <AuthFrame
      screen="suspended"
      title={t('suspended.title')}
      footer={
        <Button
          variant="outline"
          size="lg"
          className="w-full"
          onClick={() => {
            void session.signOut();
          }}
        >
          {t('suspended.signOut')}
        </Button>
      }
    >
      {/**
       * ⚠️ **NO ICON, AND THE ABSENCE IS THE FRAME'S OWN CONVENTION.** None of the seven cards
       * `AuthFrame` draws has one — the invitation card had an icon of its own before C18 folded it
       * in, and lost it in the folding. The closed icon set also has no `pause`, and the three
       * glyphs that could stand in say the wrong thing: `triangle-alert` reads as an error somebody
       * caused, `clock` as something that will resolve itself with time, and neither is true here.
       * Adding one would need the capped `ICON_ADDITIONS` decision, which is not this chunk's.
       */}
      <p className="text-muted-foreground text-center text-[13px] leading-5">
        {t('suspended.lede')}
      </p>

      <p className="text-muted-foreground text-center text-[13px] leading-5">
        {t('suspended.contact')}
      </p>

      {/* ⚠️ Who they are signed in as, because the second most likely reason somebody is looking at
          this page is that they are in the wrong account — and the sign-out control below is
          useless without knowing which account it ends. */}
      <p className="text-muted-foreground pt-1 text-center text-[11px] leading-4">
        {t('suspended.signedInAs', { email: session.email ?? '' })}
      </p>
    </AuthFrame>
  );
}
